import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { WidgetHeader, ErrorState, LoadingState } from '../../common'

import {
    UsageCostResponseBody,
    useAnalyticsApi,
    useFiltersApi,
    useUsageAndCostLabelsMap,
} from '../../../hooks'
import { useAnalyticsFiltersGetParams } from '@/features/Analytics/hooks/useAnalyticsFiltersGetParams'
import {
    useDashboardFiltersGetParams,
    useDashboardUsageAndCostFiltersStore,
} from '@/stores/useDashboardUsageAndCostFiltersStore'
import { UsageAndCostChart } from '@/features/Analytics/components/UsageAndCostChart/UsageAndCostChart'
import { ContentPlaceholder } from '@/components/molecules'
import { Badge } from '@/components/atoms'
import { formatDateForChart } from '@/utils/formatDateForChart'
import { useTotalUsns } from '@/features/Analytics/hooks/useTotalUsns'

export const DashboardUsageCostChartContainer: React.FC = () => {
    const { useUsageCostQuery } = useAnalyticsApi()
    const { useNodesQuery } = useFiltersApi()
    const { t } = useTranslation('translations')
    const dataSets = ['usage_commodityUsage', 'cost_totalInvoiced']

    const { useInvoicesSummaryQuery } = useAnalyticsApi()
    const params = useDashboardFiltersGetParams()

    const { data: invoicesSummaryData, isFetched: isInvoicesSummaryFetched } =
        useInvoicesSummaryQuery(params)

    const activeFilters = useDashboardUsageAndCostFiltersStore(
        (state) => state.activeFilters
    )
    const filters = useAnalyticsFiltersGetParams(activeFilters)

    const { data, isSuccess, isError, isFetching, isLoading, isFetched } =
        useUsageCostQuery(filters)

    const { isError: isNodesError } = useNodesQuery()

    const {
        totalUsns,
        isLoading: isUsnsLoading,
        isFetching: isUsnsFetching,
        isFetched: isUsnsFetched,
    } = useTotalUsns({
        nodeIds: filters['nodeIds[]'],
        commodityTypeId: filters.commodityTypeId,
        providerIds: filters['providerIds[]'],
        contractIds: filters['contractIds[]'],
        panIds: filters['panIds[]'],
        facilityIds: filters['facilityIds[]'],
        tagIds: filters['tagIds[]'],
        activeFilters: activeFilters,
    })

    const labelsMap = useUsageAndCostLabelsMap(filters, true)

    const renderContent = ({ data }: { data?: UsageCostResponseBody }) => {
        if (data) {
            const formattedStartDate = formatDateForChart(
                invoicesSummaryData?.currentYear.dateRange.startDate ?? ''
            )
            const formattedEndDate = formatDateForChart(
                invoicesSummaryData?.currentYear.dateRange.endDate ?? ''
            )

            return (
                <div className="grid h-widgetHeight grid-rows-[auto_1fr] gap-y-2">
                    <div className="row flex items-center gap-2">
                        <WidgetHeader
                            title={t('dashboard.usage_cost_card.title')}
                            data-testid="card-title"
                        />
                        <Badge className="text-label-xsmall inline-flex h-5 items-center !rounded-[6px]">
                            {formattedStartDate} - {formattedEndDate}
                        </Badge>
                    </div>

                    <div className="h-full min-w-minWidgetWidth">
                        <UsageAndCostChart
                            showSummaryDropdown={false}
                            data={data}
                            labelsMap={labelsMap}
                            dataSets={dataSets}
                            minHeight={false}
                            totalUsns={totalUsns}
                        />
                    </div>
                </div>
            )
        }
    }

    if (isSuccess) {
        if (!data.items.length || !dataSets.length) {
            return (
                <ContentPlaceholder
                    title={t('analytics.common.report_empty_state.title')}
                    illustrationSrc="/assets/empty-state-illustration.svg"
                    name="empty-state"
                />
            )
        }
    }

    if (isError || isNodesError) {
        return (
            <div
                className="box-border rounded-xl px-4 py-3 shadow-inset-1 shadow-neutral-200"
                data-testid="usage-cost-chart"
            >
                <div className="grid min-h-[320px] items-center justify-center">
                    <ErrorState
                        description={t('analytics.common.report_error')}
                        className="h-full"
                        boxed={false}
                    />
                </div>
            </div>
        )
    }

    return (
        <div
            className="relative box-border space-y-3 rounded-xl px-4 py-3 shadow-inset-1 shadow-neutral-200"
            data-testid="usage-cost-chart"
        >
            <div className="relative min-h-widgetHeight">
                {renderContent({ data: data })}
                {(isLoading ||
                    isFetching ||
                    !isFetched ||
                    !isInvoicesSummaryFetched ||
                    isUsnsLoading ||
                    isUsnsFetching ||
                    !isUsnsFetched) && (
                    <div className="absolute inset-0 grid items-center justify-center">
                        <LoadingState
                            message={t('analytics.common.report_chart_loading')}
                            size="small"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
